/* eslint-disable no-confusing-arrow */
import React, { useState, useCallback, useRef, lazy, Suspense } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';

// ReactCSV
import { CSVLink } from 'react-csv';

// Types
import { ApplicationState } from 'store';
import {
  SelectedEmployeesInterface,
  SelectedLinesInterface,
  SelectedCarInterface,
} from 'store/ducks/reports/types';
import { TripTypes, TripReportInterface } from 'store/ducks/trips/types';
import { LinesTypes } from 'store/ducks/lines/types';

// Actions
import { setOpen, setClose } from 'store/ducks/dialogs/actions';
import * as TripsActions from 'store/ducks/trips/actions';
import * as UtilsActions from 'store/ducks/utils/actions';
import * as LineActions from 'store/ducks/lines/actions';

// Helpers
import { oneWeekAgo } from 'helpers/date';

// Components
import { MainContent, SearchComponent, ReportTitle } from 'components';
import { TablePlaceholder } from 'components/TablePlaceholder';
import { TabPanel } from 'components/TabPanel';
import TITLES_OPTIONS from 'constants/infoModulesTitle';

// Constants
import { REPORTS_TYPES } from 'constants/report';
import ITEMS from 'constants/itemsByFilter';
import { Submissions, History } from '../../../../components';
import TableTravel from './components/TableTravel/TableTravel';

// Styles
import useStyles from './HalfTripDiaryDatails.styles';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));

interface HandleSubmitInterface {
  selectedKmType: string;
  isStartedAt: string;
  startedAt: string;
  startDate: Date;
  endDate: Date;
  selectedLines: SelectedLinesInterface[];
  selectedCars: SelectedCarInterface[];
  selectedEmployees: SelectedEmployeesInterface[];
}

const { oneDayAgo: startDate, endDate } = oneWeekAgo;

interface HalfTripDiaryDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const HalfTripDiaryDatails: React.FC<HalfTripDiaryDatailsProps> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const [tableReference, setTableReference] = useState(null);
  const [searchTripReportList, setSearchTripReportList] = useState<
    TripReportInterface[]
  >([]);
  const [tableCSV, setTableCSV] = useState('');

  const initialTripReport = useSelector(
    (state: ApplicationState) => state.trips.tripReport || [],
  );

  const startedAt = useSelector(
    (state: ApplicationState) => state.reports.startedAt,
  );

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setReportIsVisible(true);

    dispatch(
      UtilsActions.dispatchActionsChainRequest([
        {
          action: TripsActions.tripReportRequest,
          payload: {
            day: new Date(startedAt).getTime(),
            start: new Date(startDate).getTime(),
            end: new Date(endDate).getTime(),
          },
          typeSuccess: TripTypes.LIST_TRIPS_SUCCESS,
          typeFailure: TripTypes.LIST_TRIPS_FAILURE,
        },
        {
          action: LineActions.listLinesRequest,
          payload: {},
          typeSuccess: LinesTypes.LIST_LINES_SUCCESS,
          typeFailure: LinesTypes.LIST_LINES_FAILURE,
        },
      ]),
    );
  }, [dispatch, startedAt]);

  const handleApply = useCallback(
    ({
      startedAt,
      startDate,
      endDate,
      selectedLines,
      selectedCars,
      selectedEmployees,
      selectedKmType,
    }: HandleSubmitInterface) => {
      dispatch(setClose({}));
      setReportIsVisible(true);

      const isPeriod = startDate?.getTime() < endDate?.getTime();
      let startDateValue;
      let endDateValue;
      if (isPeriod) {
        startDateValue = new Date(startDate).getTime();
        endDateValue = new Date(endDate).getTime();
      } else if (startedAt) {
        startDateValue = new Date(startedAt).setHours(0, 0, 0);
        endDateValue = new Date(startedAt).setHours(23, 59, 59);
      }
      dispatch(
        TripsActions.tripReportRequest({
          start: startDateValue,
          end: endDateValue,
          employeeIds:
            selectedEmployees?.length > 0
              ? selectedEmployees.map((e) => e.id)
              : undefined,
          lineIds:
            selectedLines?.length > 0
              ? selectedLines.map((e) => e.id)
              : undefined,
          carIds:
            selectedCars?.length > 0
              ? selectedCars.map((e) => e.id)
              : undefined,
          selectedKmType,
        }),
      );

      if (!reportIsVisible) setReportIsVisible(true);
    },
    [dispatch, reportIsVisible],
  );

  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid
          container
          justifyContent='space-between'
          className={classes.gridContainer}>
          <Grid item>
            <ReportTitle
              titleVariant='h4'
              title='Relatórios de Operação • Meia Viagem'
              tooltipTitle={TITLES_OPTIONS.REPORTS.OPERATION.TRIP_HALF.VALUE}
            />
          </Grid>
          <Grid item className={classes.icons}>
            {reportIsVisible && (
              <Grid item>
                <CSVLink
                  filename='Relatório_de_meia_viagem.csv'
                  data={tableCSV}
                  ref={csvRef}>
                  <Tooltip title='Baixar CSV' placement='top'>
                    <IconButton />
                  </Tooltip>
                </CSVLink>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container className={classes.chipFilter}>
          <Box display='flex' alignItems='center' width='100%' marginTop='16px'>
            <SearchComponent
              disabledInput={!reportIsVisible}
              data={initialTripReport}
              itemsByFilter={ITEMS.ReportHalfTrip}
              results={(data: any[]) => {
                setSearchTripReportList(data);
              }}
              modalFilter={() => {
                dispatch(
                  setOpen({
                    open: true,
                    content: (
                      <Suspense fallback={<p>Carregando...</p>}>
                        <Filter
                          fields={ITEMS.halfTripDiary}
                          handleApply={handleApply}
                          notResetKmType
                        />
                      </Suspense>
                    ),
                    maxWidth: 'xl',
                  }),
                );
              }}
            />
            {/* <Grid container justifyContent='flex-end'>
              <Button
                className={classes.addButton}
                onClick={() => setIsAddSubmissiion(true)}>
                <Typography style={{ marginRight: 8, fontSize: 20 }}>
                  +
                </Typography>{' '}
                <Typography style={{ fontSize: 12 }}>Programar Novo</Typography>
              </Button>
            </Grid> */}
          </Box>
        </Grid>
        <Tabs
          indicatorColor='primary'
          onChange={handleChange}
          style={{
            background: '#ffffff',
            margin: '10px 0px 32px 16px',
          }}
          textColor='primary'
          value={generalNavGuide}>
          <Tab label='Relatório' />
          <Tab label='Envios' />
          <Tab label='Histórico' />
        </Tabs>
        <TabPanel index={0} value={generalNavGuide}>
          {!reportIsVisible && <TablePlaceholder onClick={handleLoadData} />}
          {reportIsVisible && (
            <TableTravel
              setTableCSV={setTableCSV}
              setTableReference={setTableReference}
              tableReference={tableReference}
              csvRef={csvRef}
              searchTripReportList={searchTripReportList}
            />
          )}
        </TabPanel>
        <TabPanel index={1} value={generalNavGuide}>
          <Submissions
            setSubmissionData={setSubmissionData}
            setIsEditSubmissiion={setIsEditSubmissiion}
                setIsAddSubmissiion={setIsAddSubmissiion}
            reportType={REPORTS_TYPES.TRIP}
          />
        </TabPanel>{' '}
        <TabPanel index={2} value={generalNavGuide}>
          <History reportType={REPORTS_TYPES.TRIP} />
        </TabPanel>
      </MainContent>
    </div>
  );
};

export default HalfTripDiaryDatails;
export { HalfTripDiaryDatails };
